// ── Athena Workspace Profile ───────────────────────────────────────────────────
// Converts cognitive scores into workspace visual configuration.
// Pure function — called once per profile selection and memoized in WorkspacePage.
// Does NOT create new memory, backend, or state systems.
// Reuses Fase 3 image system — only adjusts thresholds.

const ACCENT_MAP = {
  VISUAL:         '#818cf8',   // indigo
  ESTRUTURADO:    '#38bdf8',   // sky
  CONVERSACIONAL: '#34d399',   // emerald
  'PRÁTICO':      '#fb923c',   // orange
};

// Normalize raw score (max theoretical = 18: 6 questions × 3pts) → 0–10 for display bars
export function normalizeScore(raw) {
  return Math.min(10, Math.max(0, Math.round((raw || 0) / 18 * 10)));
}

export function buildWorkspaceProfile(cognitiveProfile) {
  if (!cognitiveProfile) return null;

  const { scores = {}, traits = [] } = cognitiveProfile;
  const [primary, secondary] = traits;

  const vis  = scores.VISUAL         || 0;
  const est  = scores.ESTRUTURADO    || 0;
  const conv = scores.CONVERSACIONAL || 0;
  const prat = scores['PRÁTICO']     || 0;

  const accentColor = ACCENT_MAP[primary] || '#38bdf8';

  return {
    // ── Layout density
    spacing:       vis >= 7 ? 'spacious' : conv >= 7 ? 'compact' : 'balanced',
    fontScale:     vis >= 8 ? 1.05 : conv >= 8 ? 0.96 : 1.0,
    glowLevel:     vis >= 7 ? 'high' : est >= 7 ? 'medium' : 'low',
    useCards:      vis >= 7 || est >= 7,

    // ── Image system (Fase 3 — only threshold adjusts)
    autoImages:         vis >= 8,
    visualAidThreshold: vis >= 8 ? 5 : vis >= 5 ? 9 : 99,

    // ── Response shape (already sent to backend via cognitive_directive in Fase 3)
    explanationStyle:  primary === 'CONVERSACIONAL' ? 'dialogue'
                     : primary === 'PRÁTICO'        ? 'action'
                     : primary === 'ESTRUTURADO'    ? 'steps'
                     : 'visual',
    contentChunkSize:  est >= 7 ? 'small' : conv >= 7 ? 'medium' : 'large',

    // ── UI accent
    accentColor,
    accentSoft:   `${accentColor}20`,
    accentBorder: `${accentColor}38`,

    // ── Misc behaviour
    reduceAnimations: conv >= 9 && vis <= 4,
    showProgress:     est >= 6 || prat >= 6,

    // ── Trait labels
    primaryTrait:   primary   || null,
    secondaryTrait: secondary || null,

    // ── Normalized bars for CognitiveProfilePanel
    bars: {
      VISUAL:         { score: normalizeScore(vis),  color: ACCENT_MAP.VISUAL },
      ESTRUTURADO:    { score: normalizeScore(est),  color: ACCENT_MAP.ESTRUTURADO },
      CONVERSACIONAL: { score: normalizeScore(conv), color: ACCENT_MAP.CONVERSACIONAL },
      'PRÁTICO':      { score: normalizeScore(prat), color: ACCENT_MAP['PRÁTICO'] },
    },
  };
}
